import React, { useState, useEffect } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Search, ChevronUp, ChevronDown, Filter, RefreshCw, X } from 'lucide-react';
import { getSalesRecords, getSalesDataFiles } from '../../config/api';

const PAGE_SIZE = 50;

const COLUMNS = [
  { key: 'saleDate', label: 'Sale Date', sortable: true },
  { key: 'customerName', label: 'Customer', sortable: true },
  { key: 'productNdc', label: 'NDC', sortable: true },
  { key: 'productName', label: 'Product', sortable: true },
  { key: 'quantity', label: 'Qty', sortable: true, align: 'right' },
  { key: 'unitPrice', label: 'Unit Price', sortable: true, align: 'right' },
  { key: 'totalAmount', label: 'Total', sortable: true, align: 'right' },
  { key: 'invoiceNumber', label: 'Invoice', sortable: true },
  { key: 'validationStatus', label: 'Status', sortable: true }
];

const money = (v) => (v == null ? '—' : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const num = (v) => (v == null ? '—' : Number(v).toLocaleString());
const date = (v) => (v ? new Date(v).toLocaleDateString() : '—');

const STATUS_PILL = {
  VALID: 'bg-green-100 text-green-700',
  INVALID: 'bg-red-100 text-red-700',
  PENDING: 'bg-gray-100 text-gray-600',
  WARNING: 'bg-amber-100 text-amber-700'
};

const SalesRecordsTable = () => {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('saleDate');
  const [sortOrder, setSortOrder] = useState('desc');
  const [validationStatus, setValidationStatus] = useState('ALL');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [fileId, setFileId] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Debounce the search input.
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Uploaded files (batches) for the batch filter dropdown.
  const { data: filesData } = useQuery({
    queryKey: ['sales-files-for-filter'],
    queryFn: () => getSalesDataFiles({ limit: 200 })
  });
  const files = filesData?.data?.files || [];

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['sales-records', page, search, sortBy, sortOrder, validationStatus, startDate, endDate, fileId],
    queryFn: () => getSalesRecords({
      page, limit: PAGE_SIZE, search, sortBy, sortOrder,
      validationStatus, startDate: startDate || undefined, endDate: endDate || undefined,
      fileId: fileId || undefined
    }),
    placeholderData: keepPreviousData
  });

  const records = data?.records || [];
  const pagination = data?.pagination || { total: 0, pages: 0, page: 1 };

  const activeFilterCount = [validationStatus !== 'ALL', startDate, endDate, fileId].filter(Boolean).length;
  const clearFilters = () => { setValidationStatus('ALL'); setStartDate(''); setEndDate(''); setFileId(''); setPage(1); };

  const toggleSort = (key) => {
    if (sortBy === key) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortOrder('asc');
    }
    setPage(1);
  };

  const windowStart = Math.max(1, Math.min(page - 2, pagination.pages - Math.min(5, pagination.pages) + 1));
  const pageButtons = Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => windowStart + i);

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Toolbar */}
      <div className="p-4 border-b border-gray-200 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-1 items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search NDC, product, customer, or invoice…"
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <button
            onClick={() => setShowFilters((v) => !v)}
            title="Filters"
            className={`px-3 py-2 border rounded-lg flex items-center gap-2 text-sm ${showFilters || activeFilterCount ? 'border-primary-500 text-primary-600 bg-primary-50' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
          >
            <Filter size={16} /> Filters
            {activeFilterCount > 0 && <span className="ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary-600 px-1.5 text-xs font-semibold text-white">{activeFilterCount}</span>}
          </button>
          {activeFilterCount > 0 && (
            <button onClick={clearFilters} title="Clear filters" className="px-2 py-2 text-sm text-gray-500 hover:text-gray-800 flex items-center gap-1">
              <X size={14} /> Clear
            </button>
          )}
          <button onClick={() => refetch()} title="Refresh" className="p-2 text-gray-500 hover:text-gray-800 rounded-lg hover:bg-gray-100">
            <RefreshCw size={18} className={isFetching ? 'animate-spin' : ''} />
          </button>
        </div>
        <div className="text-sm text-gray-500">{pagination.total?.toLocaleString() || 0} line items</div>
      </div>

      {showFilters && (
        <div className="p-4 border-b border-gray-200 bg-gray-50 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Batch / uploaded file</label>
            <select value={fileId} onChange={(e) => { setFileId(e.target.value); setPage(1); }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
              <option value="">All batches</option>
              {files.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.originalFileName || f.fileName} · {new Date(f.uploadDate).toLocaleDateString()} ({f._count?.salesRecords ?? f.totalRecords ?? 0})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Validation status</label>
            <select value={validationStatus} onChange={(e) => { setValidationStatus(e.target.value); setPage(1); }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
              <option value="ALL">All</option>
              <option value="VALID">Valid</option>
              <option value="INVALID">Invalid</option>
              <option value="PENDING">Pending</option>
              <option value="WARNING">Warning</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Sale date from</label>
            <input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Sale date to</label>
            <input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {COLUMNS.map((c) => (
                <th key={c.key}
                  onClick={() => c.sortable && toggleSort(c.key)}
                  className={`px-4 py-3 text-xs font-medium uppercase tracking-wider text-gray-500 whitespace-nowrap ${c.align === 'right' ? 'text-right' : 'text-left'} ${c.sortable ? 'cursor-pointer select-none hover:text-gray-800' : ''}`}
                  title={c.sortable ? `Sort by ${c.label}` : undefined}
                >
                  <span className={`inline-flex items-center gap-1 ${c.align === 'right' ? 'flex-row-reverse' : ''}`}>
                    {c.label}
                    {sortBy === c.key && (sortOrder === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading ? (
              <tr><td colSpan={COLUMNS.length} className="px-4 py-12 text-center text-gray-400">Loading…</td></tr>
            ) : records.length === 0 ? (
              <tr><td colSpan={COLUMNS.length} className="px-4 py-12 text-center text-gray-400">No sales records match your filters.</td></tr>
            ) : records.map((r) => (
              <tr key={r.id} className="hover:bg-gray-50">
                <td className="px-4 py-2.5 text-sm text-gray-600 whitespace-nowrap">{date(r.saleDate)}</td>
                <td className="px-4 py-2.5 text-sm text-gray-900">{r.customerName || r.customer?.name || '—'}</td>
                <td className="px-4 py-2.5 text-sm font-mono text-gray-700 whitespace-nowrap">{r.productNdc || '—'}</td>
                <td className="px-4 py-2.5 text-sm text-gray-700">{r.productName || r.product?.name || '—'}</td>
                <td className="px-4 py-2.5 text-sm text-gray-900 text-right">{num(r.quantity)}</td>
                <td className="px-4 py-2.5 text-sm text-gray-900 text-right">{money(r.unitPrice)}</td>
                <td className="px-4 py-2.5 text-sm font-medium text-gray-900 text-right">{money(r.totalAmount)}</td>
                <td className="px-4 py-2.5 text-sm text-gray-600 whitespace-nowrap">{r.invoiceNumber || '—'}</td>
                <td className="px-4 py-2.5 whitespace-nowrap">
                  <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_PILL[r.validationStatus] || 'bg-gray-100 text-gray-600'}`}>
                    {r.validationStatus || 'PENDING'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3">
          <span className="text-sm text-gray-500">
            Page {pagination.page} of {pagination.pages} · {pagination.total?.toLocaleString()} records
          </span>
          <nav className="flex items-center gap-1">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50 disabled:opacity-50">Prev</button>
            {pageButtons.map((p) => (
              <button key={p} onClick={() => setPage(p)}
                className={`px-3 py-1.5 text-sm rounded-md ${p === page ? 'bg-primary-600 text-white' : 'border border-gray-300 text-gray-600 hover:bg-gray-50'}`}>{p}</button>
            ))}
            <button onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))} disabled={page === pagination.pages}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50 disabled:opacity-50">Next</button>
          </nav>
        </div>
      )}
    </div>
  );
};

export default SalesRecordsTable;
